import React from "react";
import { Space, Tag, Typography } from "antd";
import { pendingEverywhere, pendingOf } from "./pending";

const { Text } = Typography;

/** « Que faire ici ? » / « Obligatoire : … » per step. */
export const GUIDE_TEXTS = {
  sources: {
    what:
      "Choisissez les établissements à fusionner et le résultat (nouvelle institution ou institution existante). Donnez son nom. Rien n'est écrit avant l'étape Appliquer.",
    must:
      "au moins deux établissements (ou un établissement et l'institution existante qui reçoit la fusion) et le nom de la nouvelle institution.",
  },
  config: {
    what:
      "Pour chaque champ de l'institution, comparez ce que chaque établissement a aujourd'hui et fixez la valeur du résultat : reprenez une valeur existante ou saisissez la vôtre. Les champs marqués « à choisir » diffèrent entre établissements et doivent être tranchés.",
    must:
      "le nom de la nouvelle institution, chaque champ qui diffère entre les établissements, chaque mois de facturation en conflit. Les champs identiques partout sont repris tels quels.",
  },
  users: {
    what:
      "Une même adresse e-mail existe sur plusieurs établissements : décidez si c'est une seule personne (fusionner — champ par champ) ou deux personnes (garder séparés — renommer un e-mail). Les autres comptes suivent simplement leur établissement.",
    must:
      "une décision par adresse e-mail en double — fusionner : le compte conservé et chaque champ qui diffère ; garder séparés : le compte renommé et sa nouvelle adresse.",
  },
  addresses: {
    what:
      "Chaque adresse est rattachée au résultat. Les doublons probables exigent une décision : garder séparés ou fusionner dans un seul ; vous pouvez aussi corriger les champs.",
    must:
      "une décision pour chaque adresse d'un doublon probable (garder ou fusionner dans une autre). Les autres adresses sont rattachées telles quelles.",
  },
  responsables: {
    what:
      "Chaque responsable est rattaché au résultat. Les doublons probables exigent une décision : garder séparés ou fusionner dans un seul ; vous pouvez aussi corriger les champs.",
    must:
      "une décision pour chaque responsable d'un doublon probable (garder ou fusionner dans un autre). Les autres responsables sont rattachés tels quels.",
  },
  blacklists: {
    what:
      "Chaque interprète exclu par un des établissements — l'institution qui reçoit comprise, quand la fusion la rejoint — avec tous les détails : qui l'exclut, où il ou elle a travaillé, où il ou elle est aussi exclu·e ailleurs. Garder l'exclusion sur le résultat (sûr) ou la retirer.",
    must:
      "garder ou retirer chaque exclusion, celles de l'institution qui reçoit comprises.",
  },
  preview: {
    what:
      "Vérifiez la liste complète : chaque enregistrement, sa valeur avant, sa valeur après, l'action. Rien ne change qui ne soit listé ici.",
    must:
      "rien — lecture seule. Un blocage signalé ici doit être levé avant d'appliquer.",
  },
  result: {
    what:
      "L'état exact avant et après, toutes décisions comprises. C'est ce que vous obtiendrez en appliquant.",
    must: "rien — lecture seule.",
  },
  apply: {
    what: "Tapez le nom pour confirmer. Réversible jusqu'à la finalisation.",
    must:
      "toutes les décisions des étapes précédentes, puis le nom de l'institution saisi à l'identique.",
  },
};

/** Titles used to prefix the pending items of earlier steps on the read-only steps. */
const STEP_TITLES = {
  config: "Configuration",
  users: "Comptes",
  addresses: "Adresses",
  responsables: "Responsables",
  blacklists: "Listes noires",
};
const READ_ONLY = ["preview", "result"];
const MAX_LISTED = 12;

/**
 * The guide at the top of every step: what to do here, what is mandatory, and the list of
 * what is still to decide — computed by `pendingOf`, exactly like the step badges and the
 * footer. `pending` overrides the computation (step « Établissements », before any preview).
 */
export function StepGuide({
  stepKey,
  preview,
  decisions,
  readiness,
  pending: pendingProp,
}) {
  const t = GUIDE_TEXTS[stepKey] || {};
  const pending = Array.isArray(pendingProp)
    ? pendingProp
    : pendingOf(stepKey, preview, decisions, { readiness });
  const earlier = READ_ONLY.includes(stepKey)
    ? pendingEverywhere(preview, decisions, STEP_TITLES, { readiness })
    : [];
  const done = pending.length === 0;
  const list = items => (
    <Space size={[4, 4]} wrap>
      {items.slice(0, MAX_LISTED).map(it => (
        <Tag key={it.key} color="red" style={{ marginRight: 0 }}>
          {it.label}
        </Tag>
      ))}
      {items.length > MAX_LISTED && (
        <Text type="secondary">… et {items.length - MAX_LISTED} autre(s)</Text>
      )}
    </Space>
  );

  return (
    <div className={`merge-guide${done ? " merge-guide-done" : ""}`}>
      <div>
        <Text className="merge-guide-title">Que faire ici ?</Text>
        <Text>{t.what}</Text>
      </div>
      {t.must && (
        <div className="merge-guide-line">
          <Text strong>Obligatoire : </Text>
          <Text>{t.must}</Text>
        </div>
      )}
      <div className="merge-guide-pending">
        {done && earlier.length > 0 ? (
          <Text type="secondary">
            Aucune décision sur cette étape — mais des étapes précédentes
            attendent encore la vôtre :
          </Text>
        ) : done ? (
          <Text strong style={{ color: "#389e0d" }}>
            Tout est décidé — vous pouvez continuer
          </Text>
        ) : (
          <Space direction="vertical" size={4}>
            <Text strong type="danger">
              Décisions restantes : {pending.length}
            </Text>
            {list(pending)}
          </Space>
        )}
        {earlier.length > 0 && (
          <div style={{ marginTop: 6 }}>
            <Text type="warning">
              Décisions restantes sur les étapes précédentes : {earlier.length}
            </Text>
            <div style={{ marginTop: 4 }}>{list(earlier)}</div>
          </div>
        )}
      </div>
    </div>
  );
}
